import { useRef, useState } from 'react';
import theme from 'theme';

const MIN_COLUMN_WIDTH = 80;

interface ColumnResizeHandleProps {
  columnType: string;
  colLegend: string;
  onResize: (columnType: string, width: number) => void;
}

const updateColumnCells = (colLegend: string, table: HTMLElement, newWidth: number) => {
  const allCells = table.querySelectorAll<HTMLElement>(`[data-column-legend="${colLegend}"]`);
  allCells.forEach(el => {
    // eslint-disable-next-line no-param-reassign
    el.style.cssText += `width:${newWidth}px;min-width:${newWidth}px;max-width:${newWidth}px;`;
  });
};

const ColumnResizeHandle = ({ columnType, colLegend, onResize }: ColumnResizeHandleProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const isResizingRef = useRef(false);

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const cell = (event.currentTarget.closest('td') || event.currentTarget.closest('th')) as HTMLElement | null;
    if (!cell) {
      return;
    }

    const table = cell.closest('table') as HTMLElement | null;
    if (!table) {
      return;
    }

    const startX = event.clientX;
    const startWidth = cell.offsetWidth;
    let currentWidth = startWidth;
    let hasMoved = false;
    isResizingRef.current = true;

    const handleMouseMove = (e: MouseEvent) => {
      hasMoved = true;
      const delta = e.clientX - startX;
      const newWidth = Math.max(MIN_COLUMN_WIDTH, startWidth + delta);
      currentWidth = newWidth;

      cell.style.width = `${newWidth}px`;
      cell.style.minWidth = `${newWidth}px`;
      cell.style.maxWidth = `${newWidth}px`;
      updateColumnCells(colLegend, table, newWidth);
    };

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      isResizingRef.current = false;

      if (hasMoved && currentWidth !== startWidth) {
        onResize(columnType, currentWidth);
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      onMouseDown={handleMouseDown}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: 'absolute',
        right: 0,
        top: 0,
        height: '100%',
        width: '6px',
        cursor: 'col-resize',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1,
      }}>
      <div
        style={{
          width: '2px',
          height: '70%',
          backgroundColor: isHovered || isResizingRef.current ? theme.palette.primary.main : 'transparent',
          transition: 'background-color 0.15s ease',
          borderRadius: '1px',
        }}
      />
    </div>
  );
};

export default ColumnResizeHandle;
